import { Container, Ticker } from 'pixi.js';
import { Signal } from 'typed-signals';
import { CircularProgressBar } from '@pixi/ui';
import { applyLayoutState } from 'libs/layouts/populateLayouts/applyLayoutState';
import { getLocalizedValue } from '@softgames/localization';
import { PiggyBankIconLayout } from '../../../../layouts/piggybank/PiggyBankIconLayout';
import { EventScheduler } from '../../../utils/EventScheduler';
import { animateBrightnessFlash } from '../../../utils/pixi/animations/filter-animation-utils';
import { FXParticleLayer } from '../../../libs/particles/FXParticleLayer';
import PiggyBankSparklesJSON from '../../../../assets/particles{ignore}/PiggyBank_Icon_Sparkles_Particles.json';
import type { FXParticleEmitterConfig } from '../../../libs/particles/FXParticleEmitterConfig';
import type { MenuIcon } from '../../../mainMenu/types';
import type { PiggyBankObservable } from '../types';
import { PiggyBankTier } from '../types';
import { getPiggyBankState } from '../PiggyBankUtils';
import { PiggyBankMoodView, PiggyBankMood } from './PiggyBankMoodView';
import { setTextAsTimerCountdown } from '../../../utils/pixi/setTextAsTimerCountdown';

export class PiggyBankIconView extends Container implements MenuIcon {
  public readonly onVisibilityChanged = new Signal<(visible: boolean) => void>();

  private readonly layout = new PiggyBankIconLayout();

  private readonly particleLayer: FXParticleLayer;

  public readonly onClicked = new Signal();

  private readonly attractScheduler = new EventScheduler();

  private readonly progressBar: CircularProgressBar;

  private progressFull = false;

  private inAttractMode = false;

  private cancelTimeUpdate: (() => void) | null = null;

  private readonly moodView: PiggyBankMoodView;

  constructor(observable?: PiggyBankObservable) {
    super();

    const { layout } = this;

    this.addChild(layout.root);

    this.progressBar = new CircularProgressBar({
      backgroundColor: 0x015cf9,
      lineWidth: 15,
      fillColor: 0x66e61d,
      fillAlpha: 1,
      backgroundAlpha: 1,
      radius: 50,
      cap: 'round',
    }).props({
      parent: this.layout.progressBarSlot,
      anchor: 0.5,
      rotation: Math.PI,
    });

    const { root, pigSlot } = this.layout;

    root.onpointertap = (): void => {
      this.onClicked.emit();
    };

    this.moodView = new PiggyBankMoodView();
    this.moodView.props({
      parent: pigSlot,
    });
    

    this.particleLayer = new FXParticleLayer().props({
      parent: layout.sparkleParticlesSlot,
    });

    this.particleLayer.addParticleEmitter(0, 0, PiggyBankSparklesJSON as FXParticleEmitterConfig);

    this.initTicker();

    if (observable) {
      this.hookUpObservable(observable);
      this.startAttractLoop();
    }
  }

  private hookUpObservable(observable: PiggyBankObservable): void {
    observable.addChangeHandler(this, () => {
      const piggyBank = getPiggyBankState(observable);
      this.updateProgress(piggyBank.coins, piggyBank.maxCoins);
      this.refreshState(piggyBank.currentTier, piggyBank.endTime);
    });
  }

  private initTicker(): void {
    const ticker = new Ticker();
    const callback = (): void => {
      this.update(ticker.deltaTime);
    };
    ticker.add(callback);
    ticker.start();

    this.on('destroyed', () => {
      ticker.destroy();
    });
  }

  public updateProgress(coins: number, maxCoins: number, instant = false): this {
    const normValue = Math.min(coins / maxCoins, 1);

    // map the value from 0-1 to 0.1-0.9
    // the progress bar is obscured by a value label
    // so we only show the circle progress from 10% to 90% as a result
    const progress = Math.round((0.1 + normValue * 0.8) * 100);

    this.tweens.create(this.progressBar).to({ progress }, instant ? 0 : 500, 'quadOut', true);
    this.layout.piggyBankCounter.text = `${getLocalizedValue(coins)} / ${getLocalizedValue(maxCoins)}`;

    this.progressFull = coins === maxCoins;

    return this;
  }

  public startAttractLoop(): void {
    if (this.inAttractMode) return;
    this.inAttractMode = true;

    const { rays } = this.layout;
    const jumpDuration = 400;
    const jumpStrength = 15;
    const jumpScaleDuration = 250;
    const bounceScaleDuration = jumpScaleDuration * 0.75;
    this.attractScheduler.addEvent(
      () => {
        this.moodView.tweenTo(
          {
            scaleY: this.moodView.scaleY * 0.9,
            scaleX: this.moodView.scaleX * 1.05,
          },
          jumpScaleDuration,
          'backIn',
          true,
          0,
          0,
          true,
        );

        this.moodView.tweenTo(
          {
            scaleY: this.moodView.scaleY * 1.05,
            scaleX: this.moodView.scaleX * 0.95,
          },
          bounceScaleDuration,
          'backIn',
          true,
          jumpScaleDuration * 2,
          0,
          true,
        );

        this.moodView.tweenTo(
          { scaleY: this.moodView.scaleY * 0.98 },
          jumpScaleDuration * 0.75,
          'backIn',
          true,
          jumpScaleDuration * 2 + bounceScaleDuration * 2,
          0,
          true,
        );

        this.moodView.tweenTo(
          { y: this.moodView.y - jumpStrength },
          jumpDuration,
          'cubicOut',
          true,
          jumpScaleDuration * 2,
          0,
          true,
        );

        if (this.progressFull) {
          animateBrightnessFlash(this.moodView, 1.45, jumpDuration, 'quadOut', jumpScaleDuration * 2);
          rays.tweenTo({ alpha: 1 }, 500, 'quadOut', true);
        } else {
          rays.tweenTo({ alpha: 0 }, 500, 'quadOut', true);
        }

        //this.refreshState();
      },
      2000,
      true,
    );
  }

  public stopAttractLoop(): void {
    if (!this.inAttractMode) return;
    this.inAttractMode = false;

    this.attractScheduler.reset();
  }

  public isVisible(): boolean {
    return this.visible;
  }

  public update(deltaTime: number): void {
    if (!this.visible) return;
    this.attractScheduler.update(deltaTime);
    this.layout.rays.rotation += 0.002 * deltaTime;
    this.particleLayer.update(deltaTime);
  }

  public refreshState(tier: PiggyBankTier, endTime: number | null): void {
    const { layout } = this;
    const { timerText } = layout;

    this.moodView.setTier(tier);
    this.moodView.setIconState(this.progressFull ? PiggyBankMood.Happy : PiggyBankMood.Normal);

    if (tier === PiggyBankTier.Legacy || !endTime) {
      applyLayoutState(layout, this.progressFull ? 'full' : 'in-progress');
    } else {
      applyLayoutState(layout, this.progressFull ? 'full_tier' : 'in-progress_tier');
    }

    this?.cancelTimeUpdate?.();

    if (endTime) {
      this.cancelTimeUpdate = setTextAsTimerCountdown(timerText, endTime);
    }
  }
}
